/**
 * Dataverse-driven cascading dropdown system for class registration
 * Replaces legacy chainedselects.js and courses.js with dynamic Dataverse data
 */
var Ntss;
(function (Ntss) {
    'use strict';
    // Enum values from Dataverse (can be serialized as numbers or strings)
    // Class status values
    const ClassStatus = {
        Draft: 1,
        Published: 2,
        Completed: 3,
        Cancelled: 4
    };
    // State code values (Active = 0, Inactive = 1)
    const StateCode = {
        Active: 0,
        Inactive: 1
    };
    // Language values (ntss_language enum from Dataverse)
    const Language = {
        English: 770780000,
        Spanish: 770780001
    };
    Ntss.previousSelections = {
        locationId: '',
        courseDefinitionId: '',
        classId: '',
        reasonId: '',
        citationCountyId: ''
    };
    /**
     * Helper to check if a state code represents "Active" status
     * Handles both numeric (0) and string ("Active") representations
     */
    function isActiveState(stateCode) {
        if (typeof stateCode === 'number') {
            return stateCode === StateCode.Active;
        }
        return stateCode === 'Active';
    }
    /**
     * Helper to check if a class status represents "Published"
     * Handles both numeric (2) and string ("Published") representations
     */
    function isPublishedStatus(status) {
        if (typeof status === 'number') {
            return status === ClassStatus.Published;
        }
        return status === 'Published';
    }
    /**
     * Helper to check if a language matches "Spanish"
     * Handles both numeric (770780001) and string ("Spanish") representations
     */
    function isSpanishLanguage(language) {
        if (language === null || language === undefined) {
            return false;
        }
        if (typeof language === 'number') {
            return language === Language.Spanish;
        }
        return language === 'Spanish';
    }
    /**
     * Helper to check if two languages match
     * Handles both numeric and string representations
     */
    function languagesMatch(lang1, lang2) {
        // Normalize both to boolean (isSpanish)
        return isSpanishLanguage(lang1) === isSpanishLanguage(lang2);
    }
    /**
     * Triggers Knockout observable update by dispatching a change event
     * This ensures KO validation stays in sync with DOM changes
     */
    function triggerKnockoutUpdate(element) {
        const event = new Event('change', { bubbles: true });
        element.dispatchEvent(event);
    }
    /**
     * Sets dropdown value and triggers Knockout update
     */
    function setDropdownValue(dropdown, value) {
        const option = Array.from(dropdown.options).find(opt => opt.value === value);
        if (option) {
            dropdown.value = value;
            triggerKnockoutUpdate(dropdown);
            return true;
        }
        return false;
    }
    /**
     * Looks up a reason name by its GUID from the class schedule data
     * Returns empty string if not found
     */
    function getReasonNameById(reasonId) {
        if (!Ntss.classScheduleData || !Ntss.classScheduleData.takingReasons || !reasonId) {
            return '';
        }
        const reason = Ntss.classScheduleData.takingReasons.find(r => r.id === reasonId);
        return reason ? reason.name : '';
    }
    Ntss.getReasonNameById = getReasonNameById;
    /**
     * Initializes the cascading dropdown system with Dataverse data
     */
    function initDataverseCascadingDropdowns(data) {
        if (!data || !data.locations || !data.courseDefinitions || !data.classes || !data.takingReasons) {
            console.error('Invalid class schedule data structure', data);
            return;
        }
        // Store data for later lookups (e.g., validation)
        Ntss.classScheduleData = data;
        const dropdowns = {
            location: document.querySelector('select[name="FormInput.ClassLocationId"]'),
            course: document.querySelector('select[name="FormInput.CourseDefinitionId"]'),
            date: document.querySelector('select[name="FormInput.ClassId"]'),
            reason: document.querySelector('select[name="FormInput.CourseTakingReasonId"]')
        };
        // Validate all dropdowns exist
        if (!dropdowns.location || !dropdowns.course || !dropdowns.date || !dropdowns.reason) {
            console.error('One or more dropdown elements not found');
            return;
        }
        // Initialize the location dropdown (only shows locations with available classes)
        populateLocations(dropdowns.location, data.locations, data.classes);
        // Initialize the county dropdown (standalone, not part of cascading)
        populateCountyDropdown(data);
        // Set up event listeners for cascading behavior
        dropdowns.location.addEventListener('change', function () {
            const locationId = this.value;
            const language = getSelectedLanguage(this);
            if (locationId) {
                populateCourses(dropdowns.course, locationId, language, data);
                clearDropdown(dropdowns.date, 'Select Class Date ...');
                clearDropdown(dropdowns.reason, 'Select Reason ...');
            }
            else {
                clearDropdown(dropdowns.course, 'Select Course ...');
                clearDropdown(dropdowns.date, 'Select Class Date ...');
                clearDropdown(dropdowns.reason, 'Select Reason ...');
            }
        });
        dropdowns.course.addEventListener('change', function () {
            const locationId = dropdowns.location.value;
            const language = getSelectedLanguage(dropdowns.location);
            const courseDefId = this.value;
            if (courseDefId && locationId) {
                populateDates(dropdowns.date, locationId, courseDefId, language, data);
                clearDropdown(dropdowns.reason, 'Select Reason ...');
            }
            else {
                clearDropdown(dropdowns.date, 'Select Class Date ...');
                clearDropdown(dropdowns.reason, 'Select Reason ...');
            }
        });
        dropdowns.date.addEventListener('change', function () {
            const courseDefId = dropdowns.course.value;
            const language = getSelectedLanguage(dropdowns.location);
            const classId = this.value;
            if (classId && courseDefId) {
                populateReasons(dropdowns.reason, courseDefId, language, data);
            }
            else {
                clearDropdown(dropdowns.reason, 'Select Reason ...');
            }
        });
        // Restore previous selections if available (for form validation failures)
        restorePreviousSelections(dropdowns, data);
    }
    Ntss.initDataverseCascadingDropdowns = initDataverseCascadingDropdowns;
    /**
     * Restores previously selected values after page reload (e.g., validation failure)
     * Populates each dropdown in sequence and selects the saved value if still valid
     */
    function restorePreviousSelections(dropdowns, data) {
        const { locationId, courseDefinitionId, classId, reasonId } = Ntss.previousSelections;
        // If no previous location, nothing to restore
        if (!locationId) {
            return;
        }
        // Try to restore location
        if (!setDropdownValue(dropdowns.location, locationId)) {
            return; // Location not available, stop restoration
        }
        // Get the language from the selected location option
        const language = getSelectedLanguage(dropdowns.location);
        // Populate and restore course
        populateCourses(dropdowns.course, locationId, language, data);
        if (!courseDefinitionId || !setDropdownValue(dropdowns.course, courseDefinitionId)) {
            return; // Course not available or not set, stop restoration
        }
        // Populate and restore date
        populateDates(dropdowns.date, locationId, courseDefinitionId, language, data);
        if (!classId || !setDropdownValue(dropdowns.date, classId)) {
            return; // Class not available or not set, stop restoration
        }
        // Populate and restore reason
        populateReasons(dropdowns.reason, courseDefinitionId, language, data);
        if (reasonId) {
            setDropdownValue(dropdowns.reason, reasonId);
        }
    }
    /**
     * Gets the selected language from a location dropdown option
     */
    function getSelectedLanguage(dropdown) {
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        if (!selectedOption || !selectedOption.dataset.language) {
            return null;
        }
        const langValue = selectedOption.dataset.language;
        // Try to parse as number (Dataverse enum value)
        const numValue = parseInt(langValue, 10);
        return isNaN(numValue) ? langValue : numValue;
    }
    /**
     * Populates the location dropdown with active locations that have available classes.
     * Each location entry includes a language (from server-side language variants).
     * The language is stored in a data attribute for downstream filtering.
     */
    function populateLocations(dropdown, locations, classes) {
        clearDropdown(dropdown, 'Select Location ...');
        // Get set of (locationId, language) pairs that have at least one available class
        const locationLanguagesWithClasses = new Set(classes
            .filter(cls => isClassAvailable(cls))
            .map(cls => `${cls.classLocationId}|${cls.language}`));
        // Filter locations to only those with available classes matching their language
        const activeLocations = locations
            .filter(loc => {
            if (!isActiveState(loc.stateCode))
                return false;
            // Check if there are classes at this location with matching language
            const key = `${loc.id}|${loc.language}`;
            return locationLanguagesWithClasses.has(key);
        })
            .sort((a, b) => (a.locationName || '').localeCompare(b.locationName || ''));
        activeLocations.forEach(location => {
            const option = document.createElement('option');
            option.value = location.id;
            option.textContent = location.locationName || 'Unknown Location';
            // Store language as data attribute for downstream filtering
            option.dataset.language = String(location.language);
            dropdown.appendChild(option);
        });
    }
    /**
     * Populates the course dropdown based on selected location and language.
     * Uses the class-level courseName for display (which may be in Spanish).
     */
    function populateCourses(dropdown, locationId, language, data) {
        clearDropdown(dropdown, 'Select Course ...');
        // Find all classes at this location with matching language
        const classesAtLocation = data.classes.filter(cls => cls.classLocationId === locationId &&
            languagesMatch(cls.language, language) &&
            isClassAvailable(cls));
        // Group classes by courseDefinitionId and get the courseName from the first class
        // (all classes for the same course/language combo should have the same courseName)
        const courseNameMap = new Map();
        classesAtLocation.forEach(cls => {
            if (!courseNameMap.has(cls.courseDefinitionId)) {
                courseNameMap.set(cls.courseDefinitionId, cls.courseName);
            }
        });
        // Get unique course definitions from these classes
        const courseDefIds = new Set(classesAtLocation.map(cls => cls.courseDefinitionId));
        const availableCourses = data.courseDefinitions
            .filter(cd => courseDefIds.has(cd.id) && isActiveState(cd.stateCode))
            .sort((a, b) => {
            // Sort by the class-level course name (may be Spanish)
            const nameA = courseNameMap.get(a.id) || a.name || '';
            const nameB = courseNameMap.get(b.id) || b.name || '';
            return nameA.localeCompare(nameB);
        });
        availableCourses.forEach(course => {
            const option = document.createElement('option');
            option.value = course.id;
            // Use the class-level courseName (may be Spanish) instead of courseDefinition.name
            option.textContent = courseNameMap.get(course.id) || course.name || 'Unknown Course';
            dropdown.appendChild(option);
        });
    }
    /**
     * Populates the date dropdown based on selected location, course, and language.
     * Uses Spanish locale for date/time formatting when language is Spanish.
     */
    function populateDates(dropdown, locationId, courseDefId, language, data) {
        clearDropdown(dropdown, 'Select Class Date ...');
        const availableClasses = data.classes
            .filter(cls => cls.classLocationId === locationId &&
            cls.courseDefinitionId === courseDefId &&
            languagesMatch(cls.language, language) &&
            isClassAvailable(cls))
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
        const useSpanishLocale = isSpanishLanguage(language);
        availableClasses.forEach(classEntity => {
            const option = document.createElement('option');
            option.value = classEntity.id;
            option.textContent = formatClassDateTime(classEntity, useSpanishLocale);
            dropdown.appendChild(option);
        });
    }
    /**
     * Populates the reason dropdown based on selected course and language.
     * Uses Spanish reason names when language is Spanish and a Spanish translation is available.
     */
    function populateReasons(dropdown, courseDefId, language, data) {
        const useSpanish = isSpanishLanguage(language);
        const placeholderText = useSpanish ? 'Seleccione Razón ...' : 'Select Reason ...';
        clearDropdown(dropdown, placeholderText);
        const availableReasons = data.takingReasons
            .filter(reason => reason.courseDefinitionId === courseDefId &&
            isActiveState(reason.stateCode))
            .sort((a, b) => {
            // Sort by the display name (Spanish if available and applicable)
            const nameA = (useSpanish && a.nameAsSpanish) ? a.nameAsSpanish : (a.name || '');
            const nameB = (useSpanish && b.nameAsSpanish) ? b.nameAsSpanish : (b.name || '');
            return nameA.localeCompare(nameB);
        });
        availableReasons.forEach(reason => {
            const option = document.createElement('option');
            option.value = reason.id;
            // Use Spanish name if available and language is Spanish, otherwise fall back to English name
            const displayName = (useSpanish && reason.nameAsSpanish) ? reason.nameAsSpanish : reason.name;
            option.textContent = displayName || 'Unknown Reason';
            dropdown.appendChild(option);
        });
    }
    /**
     * Checks if a class is available for registration
     */
    function isClassAvailable(classEntity) {
        // Check if published and active
        if (!isPublishedStatus(classEntity.status) || !isActiveState(classEntity.stateCode)) {
            return false;
        }
        // Check if class hasn't started yet
        const startDate = new Date(classEntity.startDate);
        if (startDate <= new Date()) {
            return false;
        }
        // Check if there are available seats
        const availableSeats = Math.max(0, (classEntity.maximumCapacity || 0) - (classEntity.attendeeCount || 0));
        return availableSeats > 0;
    }
    /**
     * Formats class date and time with available seats info.
     * Uses Spanish locale when useSpanishLocale is true.
     */
    function formatClassDateTime(classEntity, useSpanishLocale = false) {
        const locale = useSpanishLocale ? 'es-ES' : 'en-US';
        const startDate = new Date(classEntity.startDate);
        const endDate = new Date(classEntity.endDate);
        const dateStr = startDate.toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        const startTimeStr = startDate.toLocaleTimeString(locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        const endTimeStr = endDate.toLocaleTimeString(locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        const dayOfWeek = startDate.toLocaleDateString(locale, { weekday: 'long' });
        const availableSeats = Math.max(0, (classEntity.maximumCapacity || 0) - (classEntity.attendeeCount || 0));
        const seatsInfo = useSpanishLocale
            ? (availableSeats > 0 ? ` (${availableSeats} asientos disponibles)` : ' (Lleno)')
            : (availableSeats > 0 ? ` (${availableSeats} seats available)` : ' (Full)');
        // Format: "Dec 15, 2025 -- (Monday from 9:00 AM to 5:00 PM) (10 seats available)"
        // Spanish: "15 dic 2025 -- (lunes de 9:00 a 17:00) (10 asientos disponibles)"
        const fromToText = useSpanishLocale ? 'de' : 'from';
        const toText = useSpanishLocale ? 'a' : 'to';
        return `${dateStr} -- (${dayOfWeek} ${fromToText} ${startTimeStr} ${toText} ${endTimeStr})${seatsInfo}`;
    }
    /**
     * Clears a dropdown and adds a default placeholder option
     */
    function clearDropdown(dropdown, placeholderText) {
        dropdown.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = placeholderText;
        dropdown.appendChild(placeholder);
    }
    /**
     * Populates the citation county dropdown with US counties from Dataverse
     */
    function populateCountyDropdown(data) {
        const dropdown = document.getElementById('citationCountyDropdown');
        if (!dropdown || !data.usCounties) {
            return;
        }
        // Clear existing options except the placeholder
        dropdown.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'County where you received the Ticket';
        dropdown.appendChild(placeholder);
        // Filter and sort counties
        const activeCounties = data.usCounties
            .filter(county => isActiveState(county.stateCode))
            .sort((a, b) => {
            // Sort by state first, then by name
            const stateA = getStateName(a.state);
            const stateB = getStateName(b.state);
            const stateCompare = stateA.localeCompare(stateB);
            if (stateCompare !== 0)
                return stateCompare;
            return (a.name || '').localeCompare(b.name || '');
        });
        // Add options
        activeCounties.forEach(county => {
            const option = document.createElement('option');
            option.value = county.id;
            const stateName = getStateName(county.state);
            option.textContent = stateName ? `${county.name} - ${stateName}` : county.name;
            dropdown.appendChild(option);
        });
        // Restore previous selection if available
        if (Ntss.previousSelections.citationCountyId) {
            setDropdownValue(dropdown, Ntss.previousSelections.citationCountyId);
        }
    }
    /**
     * Gets state abbreviation from ntss_states enum value
     */
    function getStateName(state) {
        if (state === null || state === undefined)
            return '';
        // Map of ntss_states enum values to abbreviations
        // Based on typical state enum ordering (0 = AL, etc.) or string values
        const stateMap = {
            0: 'AL', 1: 'AK', 2: 'AZ', 3: 'AR', 4: 'CA', 5: 'CO', 6: 'CT', 7: 'DE',
            8: 'DC', 9: 'FL', 10: 'GA', 11: 'HI', 12: 'ID', 13: 'IL', 14: 'IN', 15: 'IA',
            16: 'KS', 17: 'KY', 18: 'LA', 19: 'ME', 20: 'MD', 21: 'MA', 22: 'MI', 23: 'MN',
            24: 'MS', 25: 'MO', 26: 'MT', 27: 'NE', 28: 'NV', 29: 'NH', 30: 'NJ', 31: 'NM',
            32: 'NY', 33: 'NC', 34: 'ND', 35: 'OH', 36: 'OK', 37: 'OR', 38: 'PA', 39: 'RI',
            40: 'SC', 41: 'SD', 42: 'TN', 43: 'TX', 44: 'UT', 45: 'VT', 46: 'VA', 47: 'WA',
            48: 'WV', 49: 'WI', 50: 'WY'
        };
        if (typeof state === 'string') {
            return state;
        }
        return stateMap[state] || '';
    }
    /**
     * Gets the county name by ID for display purposes
     */
    function getCountyNameById(countyId) {
        if (!Ntss.classScheduleData || !Ntss.classScheduleData.usCounties || !countyId) {
            return '';
        }
        const county = Ntss.classScheduleData.usCounties.find(c => c.id === countyId);
        return county ? county.name : '';
    }
    Ntss.getCountyNameById = getCountyNameById;
})(Ntss || (Ntss = {}));
